import { GetWoolContractsGql } from '@/graphql/queries/wool-data/get-wool-contracts.graphql'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { StationContext } from '@/pages/station-context'
import { Button, Group, Stack, Title } from '@mantine/core'
import { SeasonDropdown } from '@shared/components/season-dropdown/season-dropdown'
import { WoolContractsTable } from '@shared/components/wool-data/wool-contracts-table/wool-contracts-table'
import { IconPrinter } from '@tabler/icons-react'
import { useContext, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

export function WoolContracts() {
  // Get the station record number from the URL
  const { stationRecordNumber } = useParams()

  const [seasonStartDate, setSeasonStartDate] = useState<Date | undefined>(undefined)

  const seasonFilterValue: number | undefined = useMemo(
    () => seasonStartDate?.getFullYear(),
    [seasonStartDate]
  )

  // Get the station context to access the brand
  const stationContext = useContext(StationContext)

  const queryKey = useMemo(() => {
    return ['woolContractsData', stationRecordNumber, seasonFilterValue]
  }, [stationRecordNumber, seasonFilterValue])

  // Get the wool contracts data
  const { data, isLoading, isError } = useAuthenticatedGraphQuery({
    queryKey,
    gql: GetWoolContractsGql,
    queryParams: {
      stationRecordNumber,
      season: seasonFilterValue,
    },
    enabled: !!stationRecordNumber,
  })

  // Store and pass through the reference of the print button to the table component
  const printButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <Stack>
      <Group>
        <Title order={2} data-testid='wool-data-wool-contracts' mr='auto'>
          Wool Contracts
        </Title>
        <SeasonDropdown
          seasonsBeforeCurrent={5}
          seasonsAfterCurrent={4}
          setSeasonStartDate={setSeasonStartDate}
          setSeasonEndDate={() => undefined} // Season end date is currently not used by this page
        />
        <Button
          ref={printButtonRef}
          leftSection={<IconPrinter />}
          disabled={!data?.listWoolContracts?.results}
        >
          Print contracts
        </Button>
      </Group>
      <WoolContractsTable
        data={data?.listWoolContracts?.results ?? []}
        renderKey={JSON.stringify(queryKey)} // Force table re-render if station or season changes
        isLoading={isLoading}
        isError={isError}
        sumOfBalesContracted={data?.listWoolContracts?.sumOfBalesContracted}
        printButtonRef={printButtonRef}
        season={seasonFilterValue?.toString() ?? ''}
        brand={stationContext?.station.brand ?? ''}
      />
    </Stack>
  )
}
